import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { NAV } from '@/lib/navigation';

type Item = { group: string; label: string; to: string; icon: LucideIcon };

function flattenNav(): Item[] {
  const items: Item[] = [];
  for (const group of NAV) {
    for (const item of group.items) {
      items.push({ group: group.label, label: item.label, to: item.to, icon: item.icon });
    }
  }
  return items;
}

const ALL_ITEMS = flattenNav();

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();

  function close() {
    setOpen(false);
    requestAnimationFrame(() => returnFocusRef.current?.focus());
  }

  // ⌘K toggles; the TopBar search field dispatches gf:open-palette.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const isModK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isModK) {
        e.preventDefault();
        if (open) close();
        else { returnFocusRef.current = document.activeElement as HTMLElement | null; setOpen(true); }
      } else if (e.key === 'Escape' && open) {
        close();
      }
    }
    function onOpenEvent() { returnFocusRef.current = document.activeElement as HTMLElement | null; setOpen(true); }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('gf:open-palette', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('gf:open-palette', onOpenEvent);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ('');
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return ALL_ITEMS;
    const needle = q.toLowerCase();
    return ALL_ITEMS.filter((i) =>
      i.label.toLowerCase().includes(needle) || i.group.toLowerCase().includes(needle)
    );
  }, [q]);

  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIndex]);

  function go(item: Item) {
    close();
    navigate(item.to);
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && filtered[activeIndex]) {
      e.preventDefault();
      go(filtered[activeIndex]);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/25 px-4 pt-[14vh] backdrop-blur-[2px]"
      onClick={close}
    >
      <div
        className="w-full max-w-[540px] animate-pop-in overflow-hidden rounded-[14px] border border-black/[0.07] bg-surface shadow-overlay"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-divider px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-subtle" aria-hidden />
          <input
            ref={inputRef}
            aria-label="Search pages"
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIndex(0); }}
            onKeyDown={onListKeyDown}
            placeholder="Jump to a page…"
            className="w-full bg-transparent text-[14px] outline-none placeholder:text-subtle"
          />
        </div>
        <div className="max-h-[55vh] overflow-y-auto p-1.5">
          {filtered.length === 0 ? (
            <div className="px-3 py-8 text-center text-[12px] text-muted">
              No pages match “{q}”.
            </div>
          ) : (
            filtered.map((item, i) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.to}
                  onClick={() => go(item)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-left text-[12.5px] transition-colors duration-75 ${i === activeIndex ? 'bg-primary text-white' : 'text-text'}`}
                >
                  <Icon className={`h-[15px] w-[15px] shrink-0 ${i === activeIndex ? 'text-white' : 'text-text/50'}`} strokeWidth={1.9} aria-hidden />
                  <span className="flex-1 truncate">{item.label}</span>
                  <span className={`text-[10.5px] ${i === activeIndex ? 'text-white/75' : 'text-subtle'}`}>{item.group}</span>
                </button>
              );
            })
          )}
        </div>
        <div className="flex items-center justify-between border-t border-divider px-4 py-2 text-[10.5px] text-subtle">
          <span>↑↓ navigate · ↵ open · esc close</span>
          <span className="font-mono">⌘K</span>
        </div>
      </div>
    </div>
  );
}
